import { useCallback, useEffect, useRef, useState } from 'react';

/**
 * Load something from the hub and keep it fresh: on mount, when the tab regains focus, on
 * demand (after a change), and either whenever `rev` changes (the module's live revision in
 * state.ext, pushed over SSE) or, without one, every `everyMs` while visible. Errors after
 * the first success keep the last data and expose the error for an inline notice.
 */
export function useResource<T>(load: () => Promise<T>, everyMs = 30_000, rev?: number) {
  const [data, setData] = useState<T | null>(null);
  const [error, setError] = useState<unknown>(null);
  const [loading, setLoading] = useState(true);
  const loadRef = useRef(load);
  loadRef.current = load;
  const seq = useRef(0);

  const refresh = useCallback(async () => {
    const n = ++seq.current;
    try {
      const v = await loadRef.current();
      if (n === seq.current) {
        setData(v);
        setError(null);
      }
    } catch (e) {
      if (n === seq.current) setError(e);
    } finally {
      if (n === seq.current) setLoading(false);
    }
  }, []);

  const live = rev !== undefined;
  useEffect(() => {
    const onFocus = () => void refresh();
    window.addEventListener('focus', onFocus);
    const timer = live
      ? null
      : setInterval(() => {
          if (document.visibilityState === 'visible') void refresh();
        }, everyMs);
    return () => {
      window.removeEventListener('focus', onFocus);
      if (timer) clearInterval(timer);
    };
  }, [refresh, everyMs, live]);

  // On mount, and again each time the module says it changed.
  useEffect(() => {
    void refresh();
  }, [refresh, rev]);

  return { data, error, loading, refresh, setData };
}
